import React, { useState, useCallback, useEffect } from 'react'
import { Button, Modal, notification, Spin, Typography, Form, Tag } from 'antd'
import { EditFilled } from '@ant-design/icons'
import ProjectUpdate from '../ProjectUpdate'
import ProjectMap from './ProjectMap'
import { getProject, updataProject } from '../../../api/project'
import { useRequest } from '../../../hooks'
import { useParams } from 'react-router-dom'
import { KeyVal, Container } from '../../Layouts'
import { dateRender, dateTimeRender } from '../../../utils'
import { ThunderboltTwoTone, ControlTwoTone, DisconnectOutlined, WarningTwoTone } from '@ant-design/icons'
import Day, { Dayjs } from 'dayjs';
import { useApp } from '../../../model'
import { useAuthInfo } from '../../../hooks'
import IfNotObserver from '../../common/IfNotObserver';

const { Text } = Typography;

export default function ProjectSummary({ isHistory = false }) {
    const app = useApp();
    const { currentUserId } = app
    const { loadingAuthInfo, isElectrician, isManager } = useAuthInfo()
    const [showEditModal, setShowEditModal] = React.useState(false)
    const { projectId } = useParams()
    const [refreshKey, setRefreshKey] = useState(Symbol())
    const fetchProject = useCallback(() => {
        return getProject(projectId as string)
    }, [projectId, refreshKey])
    const { data: project, loading } = useRequest(fetchProject)
    const [projectEditForm] = Form.useForm()
    const [submitting, setSubmitting] = useState(false);
    const [showFileUpdateModal, setShowFileUpdateModal] = useState(false);
    useEffect(() => {
        if (project) {
            app.setState('currentProjectDetail', project)
        }
    }, [project])

    return loading || !project ? <Spin spinning /> : (
        <div>
            <div style={{ display: 'flex' }}>
                <div style={{ width: 320 }}>
                    <h3>{project?.name}</h3>
                    <div style={{ gap: 20, display: 'flex' }}>
                        {
                            isHistory ? null :
                                <IfNotObserver>
                                    <Button loading={loadingAuthInfo} disabled={isElectrician || (
                                        isManager && project.createdUser.createdBy !== currentUserId
                                    )} type='primary' onClick={() => {
                                        setShowEditModal(true)
                                    }} icon={<EditFilled />}>编辑展会</Button>
                                    <Button loading={loadingAuthInfo} disabled={isElectrician || (
                                        isManager && project.createdUser.createdBy !== currentUserId
                                    )} type='primary' onClick={() => {
                                        setShowFileUpdateModal(true)
                                    }} icon={<EditFilled />}>更新平面图</Button>
                                </IfNotObserver>
                        }
                    </div>
                    <br />
                    <div>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', textAlign: 'center', }}>
                            <Container style={{ width: '40%', }}>
                                <ThunderboltTwoTone style={{ fontSize: 30 }} />
                                <div><Text strong>{project.deviceOverview.deviceTotalCount}</Text></div>
                                <div>电箱总数</div>
                            </Container>
                            {
                                isHistory ? null :
                                    <>
                                        <Container style={{ width: '40%' }}>
                                            <ControlTwoTone style={{ fontSize: 30 }} />
                                            <div><Text strong>{project.deviceOverview.onlineSwitchCount}</Text></div>
                                            <div>在线空开</div>
                                        </Container>
                                        <Container style={{ width: '40%' }}>
                                            <DisconnectOutlined style={{
                                                fontSize: 30,
                                                color: 'rgb(22, 119, 255)'
                                            }} />
                                            <div><Text strong>{project.deviceOverview.offlineSwitchCount}</Text></div>
                                            <div>离线空开</div>
                                        </Container>
                                    </>
                            }
                            <Container style={{ width: '40%' }}>
                                <WarningTwoTone style={{ fontSize: 30 }} />
                                <div><Text strong>{project.deviceOverview.alarmHistoryTotalCount}</Text></div>
                                <div>告警总数</div>
                            </Container>
                        </div>
                    </div>
                    <br />
                    <Container>
                        <KeyVal name='展馆' value={project.organizationName} />
                        {/* <KeyVal name='网络类型' value={project.is5G ? '5G' : '4G'} /> */}
                        {
                            // project.is5G && 
                            !!project.gateways?.length &&
                            <KeyVal name='网关' value={<div style={{ display: 'inline-flex', gap: '2px 0', flexWrap: 'wrap' }}>
                                {(project.gateways || []).map(i => <Tag key={i.mac}>{i.name}</Tag>)}
                            </div>} />
                        }
                        {
                            // project.is5G && 
                            !!project.gateways?.length &&
                            <KeyVal name='网关MAC' value={<div style={{ display: 'inline-flex', gap: '2px 0', flexWrap: 'wrap' }}>
                                {(project.gateways || []).map(i => <Tag key={i.mac}>{i.mac}</Tag>)}
                            </div>} />
                        }
                        <KeyVal name='展会人员' value={<div style={{ display: 'inline-flex', gap: '2px 0', flexWrap: 'wrap' }}>
                            {(project.userList || []).map(i => <Tag key={i.userId}>{i.username}</Tag>)}
                        </div>} />
                        <KeyVal name='开始日期' value={dateRender(project.startDate)} />
                        <KeyVal name='结束日期' value={dateRender(project.endDate)} />
                        <KeyVal name='创建人' value={project.createdUser?.userName} />
                        <KeyVal name='创建时间' value={dateTimeRender(project.createdUser?.createDatetime)} />

                        <KeyVal name='备注' value={project.description || '-'} />

                    </Container>
                </div>
                <Container style={{ flex: 1, display: 'flex' }}>
                    <ProjectMap
                        isHistory={isHistory}
                        showFileUpdateModal={showFileUpdateModal}
                        setShowFileUpdateModal={setShowFileUpdateModal}
                        project={project} onUpdated={() => setRefreshKey(Symbol())} />
                </Container>
            </div>
            <Modal
                okButtonProps={{
                    loading: submitting
                }}
                okText='保存'
                onOk={() => {
                    projectEditForm.validateFields()
                        .then(values => {
                            setSubmitting(true)
                            updataProject({
                                ...project,
                                ...values,
                                startDate: (values.startDate as Dayjs).format('YYYY-MM-DD'),
                                endDate: (values.endDate as Dayjs).format('YYYY-MM-DD'),
                            })
                                .then(() => {
                                    setSubmitting(false)
                                    setShowEditModal(false)
                                    notification.success({
                                        message: '编辑成功'
                                    });
                                    setRefreshKey(Symbol())
                                }).catch((err) => {
                                    setSubmitting(false)
                                    notification.error({
                                        message: err?.message || '编辑失败,请重试'
                                    })
                                })
                        })
                        .catch(console.error)
                }}
                zIndex={99999} title='编辑展会' open={showEditModal} onCancel={() => { setShowEditModal(false) }}>
                <ProjectUpdate form={projectEditForm} isEdit initialValues={{
                    ...project,
                    users: project.userList.map(i => String(i.userId)),
                    userList: project.userList.map(i => ({
                        ...i,
                        userId: String(i.userId)
                    })),
                    startDate: Day(project.startDate),
                    endDate: Day(project.endDate),
                }} />
            </Modal>
        </div>
    )
}